import React, { useEffect, useLayoutEffect } from "react";
import { Box, styled } from "@mui/material";
import { useScrollPosition } from "@hooks";
import { BottomGradientBar, TopGradientBar } from "./components";

const ScrollableBox = styled(Box)`
  overflow-y: auto;
  height: 100%;
  position: relative;
`;

export type ScrollState = {
  isScrolledToTop: boolean;
  isScrolledToBottom: boolean;
  isScrollable: boolean;
  hasMeasured: boolean;
};

type ScrollableWithGradientsProps = {
  children: React.ReactNode;
  onScrollStateChange?: (data: ScrollState) => void;
  recalculateDeps?: React.DependencyList;
} & React.ComponentProps<typeof Box>;

export const ScrollableWithGradients = ({
  children,
  onScrollStateChange,
  recalculateDeps,
  ...boxProps
}: ScrollableWithGradientsProps) => {
  const { ref, data, recalculate } = useScrollPosition();
  const showTopGradient =
    data.hasMeasured && data.isScrollable && !data.isScrolledToTop;
  const showBottomGradient =
    data.hasMeasured && data.isScrollable && !data.isScrolledToBottom;

  useEffect(() => {
    if (!data.hasMeasured) return;
    onScrollStateChange?.(data);
  }, [data, onScrollStateChange]);

  useLayoutEffect(() => {
    if (recalculateDeps === undefined) return;
    recalculate();
    const frameId = window.requestAnimationFrame(recalculate);
    return () => window.cancelAnimationFrame(frameId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recalculateDeps is the dependency list from parent
  }, recalculateDeps ?? []);

  return (
    <ScrollableBox ref={ref} {...boxProps}>
      {showTopGradient && <TopGradientBar />}
      {children}
      {showBottomGradient && <BottomGradientBar />}
    </ScrollableBox>
  );
};
